/* eslint
  @typescript-eslint/no-unsafe-call: "off" -- `ga-gtag` package provides no types
*/
import { enrichProjectTrackingProperties } from "@/store/project/project-tracking-properties"
import * as amplitude from "@amplitude/analytics-browser"
import { Experiment, type ExperimentClient } from "@amplitude/experiment-js-client"
import * as sessionReplay from "@amplitude/session-replay-browser"
import { gtag, install } from "ga-gtag"
import { GetUserResponseBody } from "@/api/dto/users"
import { hasUserActiveSubscription } from "@/api/user-helpers"
import { Sentry } from "./sentry"
import { getRuntime } from "../utils/runtime"
import { getShellVersion, isElectronApp } from "../utils/electron"

const GLOBAL_PROPS: Record<string, string> = {
	runtime: getRuntime(),
	app_version: APP_VERSION,
}

const electronShellVersion = getShellVersion()
if (electronShellVersion) {
	GLOBAL_PROPS["electron_shell_version"] = electronShellVersion
}

const AmplitudeAPIKey =
	import.meta.env.VITE_ANALYTICS_ENVIRONMENT === "production"
		? "90e2e6454354f60e6660f9d566362c2b"
		: "6dd65b3f4eaa4bd1a4ec9d02309f495a"

const AmplitudeExperimentDeploymentKey =
	import.meta.env.VITE_ANALYTICS_ENVIRONMENT === "production"
		? "client-XybcVttHKpeuV7TQItfOLKoqA2zrHGFd"
		: "client-ezwFaZo9nJCESp1xyqAWYsGDIpEybtYQ"

const GAKey =
	import.meta.env.VITE_ANALYTICS_ENVIRONMENT === "production" ? "G-QSGKGPEF1T" : "G-ZLDYFQMMD3"

const AMPLITUDE_COOKIE_PREFIX = "AMP_"
const AMPLITUDE_COOKIE_EXPIRY = "Thu, 01 Jan 1970 00:00:00 GMT"
const AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE = 1
const AMPLITUDE_SESSION_REPLAY_WAIT_MS = 1500
const MAX_PENDING_AMPLITUDE_EVENTS = 100
const PENDING_AMPLITUDE_TTL_MS = 5 * 60 * 1000

interface PendingAmplitudeEvent {
	label: string
	fullProps: Record<string, unknown>
	enqueuedAt: number
}

let amplitudeStoragePrepared = false
let amplitudeInitialized = false
let amplitudeActivated = false
let amplitudeExperimentClient: ExperimentClient | null = null
let activeAmplitudeExperimentUserId: string | null = null
let amplitudeExperimentFetchPromise: Promise<void> | null = null
const amplitudeReplayState: {
	initialized: boolean
	initializing: boolean
	syncing: boolean
	gatingDisabled: boolean
	identityKey: string | null
	runId: number
	pendingEvents: PendingAmplitudeEvent[]
	flushTimeoutId: number | null
} = {
	initialized: false,
	initializing: false,
	syncing: false,
	gatingDisabled: false,
	identityKey: null,
	runId: 0,
	pendingEvents: [],
	flushTimeoutId: null,
}
let activeAmplitudeUserId: string | null = null
let activeAmplitudeUserSignature: string | null = null
let pendingAmplitudeOwnerUserId: string | null = null
let pendingAmplitudeEvents: PendingAmplitudeEvent[] = []
let pendingAmplitudeUserProperties: Record<string, string> = {}
let pendingAmplitudeUserPropertiesUpdatedAt: number | null = null
// A session here is one page lifetime, reset on sign-out or an identity change.
const reportedExperimentExposures = new Set<string>()
let experimentExposureUserId: string | undefined

export interface ExperimentExposure {
	experimentKey: string
	variant: "ON" | "OFF" | "FALLBACK"
	assignmentSource:
		| "backend"
		| "local_override"
		| "request_failure"
		| "missing_assignment"
		| "invalid_assignment"
}

export function prepareAmplitudeStorage() {
	if (
		amplitudeStoragePrepared ||
		typeof document === "undefined" ||
		typeof window === "undefined"
	) {
		return
	}

	clearAmplitudeCookies()
	amplitudeStoragePrepared = true
}

function initializeAmplitude() {
	if (amplitudeInitialized) {
		return
	}

	prepareAmplitudeStorage()

	amplitude.init(AmplitudeAPIKey, {
		defaultTracking: true,
		identityStorage: "localStorage",
	})

	amplitudeInitialized = true
}

function getAmplitudeExperimentClient() {
	if (!amplitudeExperimentClient) {
		amplitudeExperimentClient = Experiment.initializeWithAmplitudeAnalytics(
			AmplitudeExperimentDeploymentKey,
		)
	}

	return amplitudeExperimentClient
}

export function preloadAmplitudeExperiments(userId: string): Promise<void> {
	const experiment = getAmplitudeExperimentClient()
	if (activeAmplitudeExperimentUserId !== userId) {
		experiment.clear()
		activeAmplitudeExperimentUserId = userId
		amplitudeExperimentFetchPromise = null
	}

	if (!amplitudeExperimentFetchPromise) {
		const fetchPromise = experiment
			.fetch({ user_id: userId })
			.then(() => undefined)
			.catch((error: unknown) => {
				if (amplitudeExperimentFetchPromise === fetchPromise) {
					amplitudeExperimentFetchPromise = null
				}
				throw error
			})
		amplitudeExperimentFetchPromise = fetchPromise
	}
	return amplitudeExperimentFetchPromise
}

export async function getAmplitudeExperimentVariant(key: string, userId: string, fallback: string) {
	await preloadAmplitudeExperiments(userId)
	const experiment = getAmplitudeExperimentClient()
	return experiment.variant(key, fallback).value ?? fallback
}

function getAmplitudeSessionReplayIdentity() {
	const deviceId = amplitude.getDeviceId()
	const sessionId = amplitude.getSessionId()
	if (!deviceId || sessionId == null) {
		return null
	}

	return {
		deviceId,
		sessionId,
	}
}

function logAmplitudeSessionReplayWarning(message: string, error: unknown) {
	console.warn(`[Amplitude] ${message}`, error)
}

function flushPendingAmplitudeReplayEvents(forceWithoutReplay = false) {
	if (amplitudeReplayState.pendingEvents.length === 0) {
		if (amplitudeReplayState.flushTimeoutId != null) {
			window.clearTimeout(amplitudeReplayState.flushTimeoutId)
			amplitudeReplayState.flushTimeoutId = null
		}
		return
	}

	const replayProps =
		forceWithoutReplay || !amplitudeReplayState.initialized
			? undefined
			: sessionReplay.getSessionReplayProperties()
	const events = amplitudeReplayState.pendingEvents
	amplitudeReplayState.pendingEvents = []
	if (amplitudeReplayState.flushTimeoutId != null) {
		window.clearTimeout(amplitudeReplayState.flushTimeoutId)
		amplitudeReplayState.flushTimeoutId = null
	}

	for (const event of events) {
		amplitude.track(event.label, {
			...event.fullProps,
			...(replayProps ?? {}),
		})
	}
}

function queueAmplitudeReplayEvent(label: string, fullProps: Record<string, unknown>) {
	amplitudeReplayState.pendingEvents.push({
		label,
		fullProps,
		enqueuedAt: Date.now(),
	})
	if (amplitudeReplayState.pendingEvents.length > MAX_PENDING_AMPLITUDE_EVENTS) {
		amplitudeReplayState.pendingEvents = amplitudeReplayState.pendingEvents.slice(
			-MAX_PENDING_AMPLITUDE_EVENTS,
		)
	}

	if (amplitudeReplayState.flushTimeoutId == null) {
		amplitudeReplayState.flushTimeoutId = window.setTimeout(() => {
			amplitudeReplayState.gatingDisabled = true
			flushPendingAmplitudeReplayEvents(true)
		}, AMPLITUDE_SESSION_REPLAY_WAIT_MS)
	}
}

function initializeAmplitudeSessionReplay() {
	if (amplitudeReplayState.initialized || amplitudeReplayState.initializing) {
		return
	}

	const identity = getAmplitudeSessionReplayIdentity()
	if (!identity) {
		return
	}

	amplitudeReplayState.initializing = true
	const runId = amplitudeReplayState.runId
	void sessionReplay
		.init(AmplitudeAPIKey, {
			deviceId: identity.deviceId,
			sessionId: identity.sessionId,
			sampleRate: AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE,
		})
		.promise.then(() => {
			if (runId !== amplitudeReplayState.runId) {
				return
			}

			amplitudeReplayState.initialized = true
			amplitudeReplayState.identityKey = `${identity.deviceId}:${String(identity.sessionId)}`
			flushPendingAmplitudeReplayEvents()
		})
		.catch((error: unknown) => {
			if (runId !== amplitudeReplayState.runId) {
				return
			}

			logAmplitudeSessionReplayWarning("Session replay initialization failed.", error)
			amplitudeReplayState.initialized = false
			amplitudeReplayState.identityKey = null
			amplitudeReplayState.gatingDisabled = true
			flushPendingAmplitudeReplayEvents(true)
		})
		.finally(() => {
			if (runId === amplitudeReplayState.runId) {
				amplitudeReplayState.initializing = false
			}
		})
}

function syncAmplitudeSessionReplayIdentity() {
	if (!amplitudeReplayState.initialized) {
		return false
	}

	const identity = getAmplitudeSessionReplayIdentity()
	if (!identity) {
		return false
	}

	const nextIdentityKey = `${identity.deviceId}:${String(identity.sessionId)}`
	if (amplitudeReplayState.identityKey === nextIdentityKey) {
		return true
	}

	if (!amplitudeReplayState.syncing) {
		amplitudeReplayState.syncing = true
		const runId = amplitudeReplayState.runId
		void sessionReplay
			.setSessionId(identity.sessionId, identity.deviceId)
			.promise.then(() => {
				if (runId !== amplitudeReplayState.runId) {
					return
				}

				amplitudeReplayState.identityKey = nextIdentityKey
				flushPendingAmplitudeReplayEvents()
			})
			.catch((error: unknown) => {
				if (runId !== amplitudeReplayState.runId) {
					return
				}

				logAmplitudeSessionReplayWarning("Session replay session sync failed.", error)
			})
			.finally(() => {
				if (runId === amplitudeReplayState.runId) {
					amplitudeReplayState.syncing = false
				}
			})
	}

	return false
}

function trackAmplitudeEvent(label: string, fullProps: Record<string, unknown>) {
	if (!amplitudeReplayState.initialized) {
		initializeAmplitudeSessionReplay()
	}

	const replayReady = syncAmplitudeSessionReplayIdentity()
	if (!replayReady && !amplitudeReplayState.gatingDisabled) {
		queueAmplitudeReplayEvent(label, fullProps)
		return
	}

	amplitude.track(label, {
		...fullProps,
		...(replayReady ? sessionReplay.getSessionReplayProperties() : {}),
	})
}

function resetAmplitudeSessionReplay() {
	amplitudeReplayState.runId += 1
	amplitudeReplayState.initialized = false
	amplitudeReplayState.initializing = false
	amplitudeReplayState.syncing = false
	amplitudeReplayState.gatingDisabled = false
	amplitudeReplayState.identityKey = null
	amplitudeReplayState.pendingEvents = []
	if (amplitudeReplayState.flushTimeoutId != null) {
		window.clearTimeout(amplitudeReplayState.flushTimeoutId)
		amplitudeReplayState.flushTimeoutId = null
	}

	try {
		sessionReplay.shutdown()
	} catch (error: unknown) {
		logAmplitudeSessionReplayWarning("Session replay shutdown failed.", error)
	}
}

try {
	install(GAKey)
} catch (error) {
	console.error("[amplitude] gtag install failed:", error)
}

export function trackCheckoutSessionResultFromUrl() {
	//Parsing page load url
	const searchParams = new URLSearchParams(window.location.search)
	const from = searchParams.get("from")
	const eventLabel = searchParams.get("result")
	if (from === "stripe_checkout_session" && eventLabel) {
		const result = {}
		for (const param of searchParams) {
			result[param[0]] = param[1]
		}
		analytics.trackEvent({
			label: eventLabel,
			type: "event",
			customProperties: result,
		})

		//Restoring url to original state
		window.history.replaceState({}, document.title, window.location.pathname)
	}
}
// Registered at the bottom of the module: WebKit can fire `load` before the module graph
// finishes evaluating, so a listener alone may register too late and never run — check
// readyState and call directly in that case. (Calling it here would also hit the TDZ on
// `analytics`, declared below.)

export type EventType = "click" | "event" | "shortcut" | "error" | "system"
export type ClickType = "single" | "double" | "long" | "right_click" | "short_cut"

export interface EventProps {
	label: string
	type: EventType
	clickType?: ClickType
	value?: string
	view?: string
	breadcrumbs?: string
	errorReason?: string
	customProperties?: Record<string, unknown> | null
}

function buildAmplitudeEventPayload(props: EventProps, target?: EventTarget) {
	let breadcrumbs: string | undefined = props.breadcrumbs
	let currentView = props.view
	if (target && target instanceof Element) {
		const hierarchy = getViewHierarchy(target)
		breadcrumbs = hierarchy.join(" -> ")
		currentView = hierarchy.pop() ?? props.view
	}

	let clickType = props.clickType
	if (!clickType && props.type === "click") {
		clickType = "single"
	}

	const customProperties: Record<string, unknown> = { ...(props.customProperties ?? {}) }
	const shouldUseProjectId = props.label === "open_project"
	if (shouldUseProjectId) {
		customProperties.projectId ??= props.value
		delete customProperties.value
	}

	enrichProjectTrackingProperties(customProperties)

	const fullProps: Record<string, unknown> = {
		...GLOBAL_PROPS,
		type: props.type,
		clickType: clickType,
		label: props.label,
		...(shouldUseProjectId ? {} : { value: props.value }),
		view: currentView,
		breadcrumbs: breadcrumbs,
		errorReason: props.errorReason,
		...customProperties,
	}

	if (electronShellVersion) {
		fullProps["electronShellVersion"] = electronShellVersion
	}

	return {
		label: props.label,
		fullProps,
	}
}

function getAmplitudeUserInfoValues(user: GetUserResponseBody) {
	const hasActiveSubscription = hasUserActiveSubscription(user)

	const hasActivePurchase = (user.purchases ?? []).some(
		(purchase) => purchase.status === "active",
	)
	const normalizedEmail = user.email?.trim().toLowerCase()
	const isEmployee =
		normalizedEmail?.endsWith("@pixieset.com") === true ||
		normalizedEmail?.endsWith("@polarr.co") === true
	const externalId = user.externalId

	return {
		userId: user.userId,
		displayName: user.displayName,
		email: user.email,
		hasActiveSubscription,
		hasActivePurchase,
		isEmployee,
		externalId,
		hasAuthenticatedElectron: isElectronApp,
	}
}

function getAmplitudeUserInfoSignature(user: GetUserResponseBody) {
	return JSON.stringify(getAmplitudeUserInfoValues(user))
}

function shouldApplyAmplitudeUserInfo(user: GetUserResponseBody) {
	return activeAmplitudeUserSignature !== getAmplitudeUserInfoSignature(user)
}

function applyAmplitudeUserInfo(user: GetUserResponseBody) {
	const amplitudeUserInfo = getAmplitudeUserInfoValues(user)
	amplitude.setUserId(amplitudeUserInfo.userId)

	const identifyEvent = new amplitude.Identify()
	identifyEvent.set("has_active_purchase", amplitudeUserInfo.hasActivePurchase)
	identifyEvent.set("has_active_subscription", amplitudeUserInfo.hasActiveSubscription)
	identifyEvent.set("is_employee", amplitudeUserInfo.isEmployee)
	identifyEvent.set("external_id", amplitudeUserInfo.externalId)
	amplitudeUserInfo.hasAuthenticatedElectron &&
		identifyEvent.set("has_authenticated_electron", true)

	amplitude.identify(identifyEvent)
	activeAmplitudeUserId = amplitudeUserInfo.userId
	activeAmplitudeUserSignature = getAmplitudeUserInfoSignature(user)
}

function applyAmplitudeUserProperties(properties: Record<string, string>) {
	if (Object.keys(properties).length === 0) {
		return
	}

	const identifyEvent = new amplitude.Identify()
	for (const [key, value] of Object.entries(properties)) {
		identifyEvent.set(key, value)
	}
	amplitude.identify(identifyEvent)
}

function flushPendingAmplitudeUserProperties() {
	if (Object.keys(pendingAmplitudeUserProperties).length === 0) {
		return
	}

	const properties = pendingAmplitudeUserProperties
	pendingAmplitudeUserProperties = {}
	pendingAmplitudeUserPropertiesUpdatedAt = null
	applyAmplitudeUserProperties(properties)
}

function flushPendingAmplitudeEvents() {
	if (pendingAmplitudeEvents.length === 0) {
		return
	}

	const events = pendingAmplitudeEvents
	pendingAmplitudeEvents = []
	for (const event of events) {
		trackAmplitudeEvent(event.label, event.fullProps)
	}
}

function clearPendingAmplitudeState() {
	pendingAmplitudeOwnerUserId = null
	pendingAmplitudeEvents = []
	pendingAmplitudeUserProperties = {}
	pendingAmplitudeUserPropertiesUpdatedAt = null
}

function purgeExpiredPendingAmplitudeState() {
	const now = Date.now()
	pendingAmplitudeEvents = pendingAmplitudeEvents.filter(
		(event) => now - event.enqueuedAt <= PENDING_AMPLITUDE_TTL_MS,
	)

	if (
		pendingAmplitudeUserPropertiesUpdatedAt != null &&
		now - pendingAmplitudeUserPropertiesUpdatedAt > PENDING_AMPLITUDE_TTL_MS
	) {
		pendingAmplitudeUserProperties = {}
		pendingAmplitudeUserPropertiesUpdatedAt = null
	}

	if (
		pendingAmplitudeEvents.length === 0 &&
		Object.keys(pendingAmplitudeUserProperties).length === 0
	) {
		pendingAmplitudeOwnerUserId = null
	}
}

function ensurePendingAmplitudeOwner(userId: string) {
	purgeExpiredPendingAmplitudeState()

	if (pendingAmplitudeOwnerUserId && pendingAmplitudeOwnerUserId !== userId) {
		clearPendingAmplitudeState()
	}

	pendingAmplitudeOwnerUserId = userId
}

export function activateAmplitudeForUser(user: GetUserResponseBody) {
	ensurePendingAmplitudeOwner(user.userId)

	const hasPendingAmplitudeUserProperties = Object.keys(pendingAmplitudeUserProperties).length > 0
	const hasPendingAmplitudeEvents = pendingAmplitudeEvents.length > 0
	if (
		amplitudeActivated &&
		activeAmplitudeUserId === user.userId &&
		!hasPendingAmplitudeUserProperties &&
		!hasPendingAmplitudeEvents
	) {
		return
	}

	initializeAmplitude()

	if (shouldApplyAmplitudeUserInfo(user)) {
		applyAmplitudeUserInfo(user)
	}
	initializeAmplitudeSessionReplay()
	flushPendingAmplitudeUserProperties()
	amplitudeActivated = true
	flushPendingAmplitudeEvents()
	pendingAmplitudeOwnerUserId = null
}

export function deactivateAmplitudeUntilNextUser() {
	reportedExperimentExposures.clear()
	experimentExposureUserId = undefined
	clearPendingAmplitudeState()
	amplitudeActivated = false
	activeAmplitudeUserId = null
	activeAmplitudeUserSignature = null
	activeAmplitudeExperimentUserId = null
	amplitudeExperimentFetchPromise = null
	amplitudeExperimentClient?.clear()

	resetAmplitudeSessionReplay()

	if (amplitudeInitialized) {
		amplitude.reset()
	}
}

export const analytics = {
	trackExperimentExposure: (exposure: ExperimentExposure, userId?: string) => {
		if (userId !== undefined) {
			if (experimentExposureUserId !== undefined && experimentExposureUserId !== userId) {
				reportedExperimentExposures.clear()
			}
			experimentExposureUserId = userId
		}
		const key = JSON.stringify([
			exposure.experimentKey,
			exposure.variant,
			exposure.assignmentSource,
		])
		if (reportedExperimentExposures.has(key)) return
		reportedExperimentExposures.add(key)
		analytics.trackEvent({
			label: "experiment_exposure",
			type: "event",
			customProperties: {
				experiment_key: exposure.experimentKey,
				variant: exposure.variant,
				assignment_source: exposure.assignmentSource,
				exposed_at: new Date().toISOString(),
			},
		})
	},
	trackEvent: (props: EventProps, target?: EventTarget) => {
		const { label, fullProps } = buildAmplitudeEventPayload(props, target)

		if (amplitudeActivated) {
			trackAmplitudeEvent(label, fullProps)
		} else {
			purgeExpiredPendingAmplitudeState()
			pendingAmplitudeEvents.push({ label, fullProps, enqueuedAt: Date.now() })
			if (pendingAmplitudeEvents.length > MAX_PENDING_AMPLITUDE_EVENTS) {
				pendingAmplitudeEvents = pendingAmplitudeEvents.slice(-MAX_PENDING_AMPLITUDE_EVENTS)
			}
		}

		gtag("event", label, fullProps)
	},
	setUserInfo: (user: GetUserResponseBody) => {
		const amplitudeUserInfo = getAmplitudeUserInfoValues(user)
		Sentry.setUser({
			id: amplitudeUserInfo.userId,
			username: amplitudeUserInfo.displayName,
			email: amplitudeUserInfo.email,
		})

		if (amplitudeActivated) {
			if (shouldApplyAmplitudeUserInfo(user)) {
				applyAmplitudeUserInfo(user)
			}
		} else {
			ensurePendingAmplitudeOwner(user.userId)
		}

		gtag("set", "user_properties", {
			user_id: amplitudeUserInfo.userId,
			has_active_purchase: amplitudeUserInfo.hasActivePurchase,
			has_active_subscription: amplitudeUserInfo.hasActiveSubscription,
			is_employee: amplitudeUserInfo.isEmployee,
			external_id: amplitudeUserInfo.externalId,
		})

		amplitudeUserInfo.hasAuthenticatedElectron &&
			gtag("set", "user_properties", { has_authenticated_electron: true })
	},
	setUserProperties: (properties: Record<string, string>) => {
		if (amplitudeActivated) {
			applyAmplitudeUserProperties(properties)
		} else {
			purgeExpiredPendingAmplitudeState()
			pendingAmplitudeUserProperties = {
				...pendingAmplitudeUserProperties,
				...properties,
			}
			pendingAmplitudeUserPropertiesUpdatedAt = Date.now()
		}

		gtag("set", "user_properties", properties)
	},
}

function getViewHierarchy(el?: Element | HTMLElement | null) {
	const hierarchy: string[] = []
	let current = el?.closest(`[aria-label]`)
	while (current) {
		const name = current?.getAttribute("aria-label")
		if (name) {
			hierarchy.unshift(name)
		}
		current = current.parentElement?.closest(`[aria-label]`)
	}
	return hierarchy
}

function clearAmplitudeCookies() {
	const cookieNames = new Set(
		document.cookie
			.split(";")
			.map((cookie) => cookie.trim().split("=")[0]?.trim() ?? "")
			.filter((cookieName) => cookieName.startsWith(AMPLITUDE_COOKIE_PREFIX)),
	)

	const domainCandidates = getCookieDomainCandidates(window.location.hostname)
	for (const cookieName of cookieNames) {
		expireCookie(cookieName)
		for (const domain of domainCandidates) {
			expireCookie(cookieName, domain)
		}
	}
}

function getCookieDomainCandidates(hostname: string) {
	if (!hostname) {
		return []
	}

	const candidates = new Set<string>([hostname, `.${hostname}`])
	const parts = hostname.split(".").filter(Boolean)

	for (let index = 1; index < parts.length; index++) {
		const parentDomain = parts.slice(index).join(".")
		candidates.add(parentDomain)
		candidates.add(`.${parentDomain}`)
	}

	return Array.from(candidates)
}

function expireCookie(name: string, domain?: string) {
	const domainAttribute = domain ? `; domain=${domain}` : ""
	document.cookie = `${name}=; expires=${AMPLITUDE_COOKIE_EXPIRY}; path=/${domainAttribute}`
}

if (document.readyState === "complete") {
	trackCheckoutSessionResultFromUrl()
} else {
	window.addEventListener("load", trackCheckoutSessionResultFromUrl)
}
